//@ts-nocheck
import { useState } from 'react';
import type { NextPage } from 'next';
import Link from 'next/link';
import Image from 'next/image';
import { BailetsBlock } from '../../ModalCopy.styles';
import { CloseVideoIcon, LogoMartins } from '@assets/svgs';
import { CWriting } from '@data/copywriting';

type BreitlingProps = {
  isClosed?: () => void;
  showImg?: boolean | string;
  showMedia?: boolean | string;
};

export const Breitling = ({
  isClosed,
  showImg,
  showMedia
}: BreitlingProps) => {
  return (
    <>
      <BailetsBlock>
        <CloseVideoIcon
          animation="closeIcon"
          color="#000"
          isActive={isClosed}
        />
        <LogoMartins
          width={70}
          height={65}
          color="rgba(0,0,0,0.5)"
          animation="mansion"
        />

        <h3>BREITLING FOR BENTLEY</h3>
        <span style={{ marginBottom: '25px' }}>
          BREITLING FOR BENTLEY / PREMIER MULLINER
        </span>
        {showImg && <Image src={showImg} width={1480} height={825} alt="" />}
        {showMedia && (
          <video
            controlsList="nodownload"
            controls={true}
            playsInline
            muted
            autoPlay
            loop
          >
            <source src={showMedia} type="video/mp4" />
          </video>
        )}
        <h3 style={{ marginTop: '25px' }}>{CWriting.copyWriting[3].title1}</h3>
        <h3>{CWriting.copyWriting[3].title2}</h3>
        <p>
          <b>Classic sophistication. Time is unstoppable. STAY ON TIME.</b>
          <br />
          <br /> Introducing the Breitling for Bentley Premier Mulliner Edition,
          where Swiss precision meets British luxury. Inspired by the exquisite
          Bentley Continental GT Mulliner Convertible, this timepiece is a
          testament to unparalleled craftsmanship and design. The blue dial,
          accented with red and white, mirrors Bentley’s bespoke interiors,
          while the knurled bezel echoes the iconic detailing found in every
          Mulliner creation. Powered by the Breitling Manufacture Caliber 01,
          this COSC-certified chronometer ensures precision and reliability for
          the discerning collector. With its limited edition exclusivity, owning
          this watch is not just about keeping time, but celebrating a legacy of
          excellence and elegance. Elevate your journey with the Premier
          Mulliner Edition—where every second is a statement.
        </p>
      </BailetsBlock>

      <BailetsBlock>
        <h2 className="markTitle">Case Study: Breitling for Bentley Premier Mulliner Edition Watch</h2>
        <p style={{ width: '100%' }}>
          <b>Brand:</b> Breitling for Bentley
          <br />
          <br />
          <b>Product:</b> Premier Mulliner Edition Watch
          <br />
          <br />
          <b>Objective:</b> Illustrate how Breitling and Bentley successfully
          leveraged an integrated marketing campaign to increase market
          penetration of the Breitling for Bentley Premier Mulliner Edition
          Watch among affluent millennials.
        </p>
      </BailetsBlock>

      <BailetsBlock style={{ justifyContent: 'center' }}>
        <h2 className="markTitle">Challenge</h2>
        <p style={{ width: '100%' }}>
          <b>Increasing Market Penetration Among Affluent Millennials</b>
          <br />
          Breitling and Bentley have a long-standing collaboration, primarily
          appealing to an older demographic of watch collectors and luxury car
          enthusiasts. However, with the introduction of the Premier Mulliner
          Edition, the brands aimed to capture the attention of affluent
          millennials who prioritize unique, high-quality products that align
          with their aspirational lifestyles.
          <br />
          <br />
          <b>1- Technical Complexity:</b> Communicating the sophisticated
          technical features of the watch in a way that is accessible and
          engaging to a younger audience not necessarily familiar with high-end
          watchmaking.
          <br />
          <br />
          <b>2- Luxury Perception:</b> Differentiating the watch in a crowded
          luxury market where millennials are often targeted with a multitude of
          premium products.
          <br />
          <br />
          <b>3- Digital Engagement:</b> Effectively utilizing digital channels
          to reach and resonate with tech-savvy millennials who primarily
          consume information and make purchasing decisions online.
        </p>
      </BailetsBlock>

      <BailetsBlock style={{ justifyContent: 'center' }}>
        <h2 className="markTitle">Solution</h2>
        <p style={{ width: '100%' }}>
          <b>
            Integrated Marketing Campaign Focused on Storytelling and Digital
            Engagement
          </b>
          <br />
          <br />
          <b>1- Simplified Technical Communication:</b>
          <br />
          <b>- Video Series:</b> Created a series of short, visually appealing
          videos explaining the watch’s features. Each video focused on a single
          aspect, such as the Breitling Manufacture Caliber 01 movement or the
          design inspiration from Bentley’s Mulliner division. These videos used
          simple language and analogies to make the technical details more
          relatable.
          <br />
          <br />
          <b>- Interactive Microsite:</b> Developed an interactive microsite
          where users could explore the watch in a 360-degree view. Each part of
          the watch was clickable, providing pop-up explanations and videos
          about its features and craftsmanship.
          <br />
          <br />
          <b>2- Enhanced Luxury Perception:</b>
          <br />
          <b>- Influencer Partnerships:</b> Partnered with well-known luxury
          lifestyle influencers and content creators who resonate with the
          millennial demographic. These influencers shared their personal
          stories and experiences with the watch, emphasizing its exclusivity
          and craftsmanship.
          <br />
          <br />
          <b>- Storytelling Campaign:</b> Launched a digital storytelling
          campaign titled "Time and Craft," highlighting the journey of the
          watch from design to production. The campaign featured artisans and
          designers from both Breitling and Bentley, showcasing the meticulous
          attention to detail and the synergy between the two brands.
          <br />
          <br />
          <b>Maximized Digital Engagement:</b>
          <br />
          <b>- Social Media Strategy:</b> Implemented a robust social media
          strategy across platforms like Instagram, YouTube, and TikTok. This
          included behind-the-scenes content, live Q&A sessions with
          watchmakers, and user-generated content campaigns encouraging owners
          to share their own stories.
          <br />
          <br />
          <b>- E-Commerce Integration:</b> Enhanced the online purchasing
          experience by integrating augmented reality (AR) features that allowed
          potential buyers to “try on” the watch virtually. Additionally,
          offered personalized virtual consultations with Breitling experts to
          answer questions and guide purchases.
        </p>
      </BailetsBlock>

      <BailetsBlock style={{ justifyContent: 'center' }}>
        <h2 className="markTitle">Results</h2>
        <p style={{ width: '100%' }}>
          <b>1- Increased Engagement:</b> The video series and interactive
          microsite collectively received over 2 million views within the first
          month, with high engagement rates indicating that the simplified
          technical explanations were well-received.
          <br />
          <br />
          <b>2- Improved Perception and Reach:</b> Influencer posts reached a
          combined audience of over 10 million, with significant positive
          feedback and increased brand awareness among millennials.
          <br />
          <br />
          <b>3- Boosted Sales and Inquiries:</b> The campaign led to a 35%
          increase in inquiries and a 20% rise in online sales for the Premier
          Mulliner Edition within the first three months. The AR feature was
          particularly popular, with over 50,000 users trying the virtual watch
          on.
          <br />
          <br />
          <b>4- Enhanced Brand Loyalty:</b> The storytelling campaign fostered a
          deeper connection with the brand, reflected in a 25% increase in
          social media followers and a high volume of user-generated content,
          further amplifying the campaign’s reach and impact.
        </p>
      </BailetsBlock>

      <BailetsBlock style={{ justifyContent: 'center' }}>
        <h2 className="markTitle">Conclusion</h2>
        <p style={{ width: '100%' }}>
          The integrated marketing campaign successfully addressed the
          challenges of engaging affluent millennials by simplifying technical
          communication, enhancing the perception of luxury through
          storytelling, and maximizing digital engagement. This approach not
          only increased market penetration but also strengthened Breitling and
          Bentley's brand loyalty among a younger, tech-savvy audience.
        </p>
      </BailetsBlock>
    </>
  );
};
